import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

// Enable the relativeTime plugin
dayjs.extend(relativeTime);

interface AlarmTooltipProps {
    alarm: string;
    startTime: number;
    endTime: number;
    active?: boolean;
    payload?: any[];
    label?: any;
}

export const AlarmTooltip: React.FC<AlarmTooltipProps> = ({ alarm, startTime, endTime, active }) => {
    if (!active) return null;

    const duration = endTime - startTime;
    const durationHours = Math.floor(duration / (1000 * 60 * 60));
    const durationMinutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));

    // Parse alarm details
    const alarmParts = alarm.split('=');
    const alarmType = alarmParts[0] || 'Unknown';
    const alarmDescription = alarmParts[1] || alarm;

    return (
        <Paper
            elevation={8}
            sx={{
                p: 2,
                // backgroundColor: 'rgba(65, 36, 36, 0.98)',
                border: '2px solid #ff4d4f',
                borderRadius: 2,
                minWidth: 280,
                maxWidth: 400,
                backdropFilter: 'blur(8px)',
                boxShadow: '0 8px 32px rgba(255, 77, 79, 0.3)',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box
                    sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: '#ff4d4f',
                        borderRadius: '50%',
                        mr: 1.5,
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                            '0%': { opacity: 1 },
                            '50%': { opacity: 0.5 },
                            '100%': { opacity: 1 },
                        },
                    }}
                />
                <Typography
                    variant="h6"
                    sx={{
                        color: '#d61313',
                        fontWeight: 'bold',
                        fontSize: '1rem'
                    }}
                >
                    Active Alarm
                </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Alarm Type:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#1976d2' }}>
                    {alarmType}
                </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Description:
                </Typography>
                <Typography variant="body1" color="primary" sx={{ fontWeight: 'medium' }}>
                    {alarmDescription}
                </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Start Time: 
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                        {dayjs(startTime).format('MMM DD, HH:mm')}
                    </Typography>
                </Box>
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        End Time:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                        {dayjs(endTime).format('MMM DD, HH:mm')}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{
                backgroundColor: 'rgba(255, 77, 79, 0.1)',
                borderRadius: 1,
                p: 1.5,
                borderLeft: '4px solid #ff4d4f'
            }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Duration:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#d61313' }}>
                    {durationHours > 0 && `${durationHours}h `}
                    {durationMinutes}m
                    {durationHours === 0 && durationMinutes === 0 && '< 1m'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {dayjs(startTime).fromNow()}
                </Typography>
            </Box>
        </Paper>
    );
};

export default AlarmTooltip;